import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Timer, Heart } from 'lucide-react';

const features = [
  {
    icon: Target,
    title: 'The ONE Thing',
    description: 'Define your absolute priority for the day. Keep it front and center until it\'s done. No distractions, no excuses.',
    gradient: 'from-primary/20 to-primary/5',
  },
  {
    icon: Timer,
    title: 'Focus Timer',
    description: 'Built-in Pomodoro timer to track deep work sessions. See exactly where your time goes—focus vs admin work.',
    gradient: 'from-secondary/20 to-secondary/5',
  },
  {
    icon: Heart,
    title: 'Mood & Momentum',
    description: '"How\'s your day going?" Check-ins that correlate your mood with your productivity. Understand your patterns.',
    gradient: 'from-success/20 to-success/5',
  },
];

export function FeaturesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} id="features" className="py-24 sm:py-32 relative">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Built for How You <span className="text-primary">Actually</span> Work
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three powerful pillars that transform scattered intentions into completed accomplishments.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-b opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl blur-xl" 
                   style={{ background: `linear-gradient(to bottom, hsl(var(--primary) / 0.1), transparent)` }} />
              
              <div className="relative h-full bg-card border border-border/50 rounded-2xl p-6 lg:p-8 hover:border-primary/30 transition-colors duration-300">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="h-7 w-7 text-foreground" />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                
                {/* Decorative line */}
                <div className="absolute bottom-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
